<script lang="ts" setup>
const { reason, isRetrying, retry } = usePlatformSessionGate()

const titleRef = ref<HTMLElement | null>(null)

const isAuthFailed = computed(() => reason.value === 'auth-failed')

const title = computed(() => {
  switch (reason.value) {
    case 'auth-failed':
      return 'Не удалось войти'
    case 'missing-host':
      return 'Идентификатор канала не задан'
    case 'no-payload':
    case null:
      return 'Откройте из Telegram или VK'
    default: {
      const _exhaustive: never = reason.value
      return _exhaustive
    }
  }
})

const description = computed(() => {
  switch (reason.value) {
    case 'auth-failed':
      return 'Попробуйте ещё раз. Если ошибка повторяется, откройте магазин из Telegram или VK.'
    case 'missing-host':
      return 'Приложение не знает, к какому каналу подключаться. Задайте идентификатор для этой платформы.'
    case 'no-payload':
    case null:
      return 'Этот магазин работает как Mini App. Откройте его через Telegram или VK.'
    default: {
      const _exhaustive: never = reason.value
      return _exhaustive
    }
  }
})

const icon = computed(() => {
  switch (reason.value) {
    case 'auth-failed':
    case 'missing-host':
      return 'i-lucide-circle-alert'
    case 'no-payload':
    case null:
      return 'i-lucide-smartphone'
    default: {
      const _exhaustive: never = reason.value
      return _exhaustive
    }
  }
})

const statusMessage = computed(() =>
  isRetrying.value ? 'Повторный вход…' : title.value,
)

onMounted(() => {
  titleRef.value?.focus()
})
</script>

<template lang="html">
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-default p-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] pl-[max(1.5rem,env(safe-area-inset-left))] pr-[max(1.5rem,env(safe-area-inset-right))]"
    role="main"
    data-testid="platform-gate"
    :data-gate-reason="reason ?? undefined"
  >
    <div
      role="status"
      aria-live="polite"
      :aria-busy="isRetrying"
    >
      <!--
        UEmpty's default title slot is wrapped in <h2>; use #header so the
        page title can be a real <h1> without nested headings.
      -->
      <UEmpty
        variant="naked"
        size="lg"
        :description="description"
      >
        <template #header>
          <div class="flex flex-col items-center gap-2 max-w-sm text-center">
            <UIcon
              :name="isRetrying ? 'i-lucide-loader-circle' : icon"
              class="size-11 shrink-0 mb-2 text-[22px]"
              :class="{ 'animate-spin': isRetrying }"
            />
            <h1
              ref="titleRef"
              tabindex="-1"
              class="text-highlighted text-pretty font-medium text-base outline-none"
            >
              {{ title }}
            </h1>
          </div>
        </template>

        <template
          v-if="isAuthFailed"
          #actions
        >
          <UButton
            label="Повторить"
            icon="i-lucide-refresh-cw"
            color="neutral"
            variant="solid"
            :loading="isRetrying"
            :disabled="isRetrying"
            data-testid="platform-gate-retry"
            @click="retry"
          />
        </template>
      </UEmpty>

      <span class="sr-only">{{ statusMessage }}</span>
    </div>
  </div>
</template>
